import { useMemo, useState } from 'react';

import parse, { DOMNode, Element } from 'html-react-parser';
import { marked } from 'marked';

import { ArtifactBlock } from './artifact-block';
import { EndBlock } from './end-block';
import { Reference } from './reference';
import { UserMessage } from './user-message';
import {
  ArtifactBlock as ArtifactBlockType,
  ContentBlock,
  KimMessageType,
  ReferenceType,
} from '../../../../../../../../../../../backend/src/types/types';
import { cn } from '@/new/lib/utils';

export const REF_PLACEHOLDER_TAG = 'kimref';
const ARTIFACT_PLACEHOLDER_TAG = 'kimartifact';

export function formatContent(blocks: ContentBlock[]): string {
  return blocks
    .map((block) => {
      if (block.type === 'text') return block.value;
      if (block.type === 'artifact')
        return `<${ARTIFACT_PLACEHOLDER_TAG} data-id="${block.id}-${block.versionNumber}"></${ARTIFACT_PLACEHOLDER_TAG}>`;
      return `<${REF_PLACEHOLDER_TAG} data-id="${block.id}" data-ref-type="${block.refType}"></${REF_PLACEHOLDER_TAG}>`;
    })
    .join('');
}

export function KimMessage({
  type,
  content,
  isComplete,
  referenceIndexMap,
  onReferenceClick,
  artifactsById,
  latestArtifactId,
}: {
  type: KimMessageType;
  content: ContentBlock[];
  isComplete: boolean;
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
  artifactsById?: Map<string, ArtifactBlockType>;
  latestArtifactId?: string | null;
}) {
  const isUser = type === KimMessageType.User;
  const [copied, setCopied] = useState(false);

  const factIds = useMemo(() => {
    const ids = new Set<string>();
    for (const block of content) {
      if (block.type === 'reference' && block.refType === ReferenceType.Fact) {
        ids.add(block.id);
      }
    }
    return Array.from(ids);
  }, [content]);

  const rendered = useMemo(() => {
    // Build a single markdown string with placeholders for references
    const combined = formatContent(content);

    const rawHtml = marked.parse(combined, { async: false }) as string;
    // Remove spaces between refs
    const html = rawHtml.replace(new RegExp(`(<\\/${REF_PLACEHOLDER_TAG}>)\\s+(<${REF_PLACEHOLDER_TAG})`, 'g'), '$1$2');

    return parse(html, {
      replace: (domNode: DOMNode) => {
        if (domNode instanceof Element && domNode.name === REF_PLACEHOLDER_TAG) {
          const id = domNode.attribs['data-id'];
          const refType = domNode.attribs['data-ref-type'] as ReferenceType;
          return (
            <Reference
              index={referenceIndexMap.get(id)?.index ?? 0}
              id={id}
              referenceType={refType}
              onClick={
                refType === ReferenceType.Fact || refType === ReferenceType.Chunk || refType === ReferenceType.Document
                  ? onReferenceClick
                  : undefined
              }
            />
          );
        }
        if (domNode instanceof Element && domNode.name === ARTIFACT_PLACEHOLDER_TAG) {
          const id = domNode.attribs['data-id'];
          const artifact = id ? artifactsById?.get(id) : undefined;
          if (!artifact) return null;
          return (
            <ArtifactBlock
              id={id}
              content={artifact.content}
              referenceIndexMap={referenceIndexMap}
              onReferenceClick={onReferenceClick}
              collapsed={!!id && id !== latestArtifactId}
              version={artifact?.versionNumber}
            />
          );
        }
      },
    });
  }, [content, referenceIndexMap, onReferenceClick, artifactsById, latestArtifactId]);

  const copyToClipboard = () => {
    const markdown = content
      .filter((block) => block.type === 'text')
      .map((block) => block.value)
      .join('');
    navigator.clipboard.writeText(markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (isUser) {
    return <UserMessage content={content} />;
  }

  return (
    <div className="flex">
      <div
        className={cn(
          // the [font-feature-settings:\'calt\'_0] disables converting (1) into a weird unicode char
          "w-full rounded-xl py-2 [font-feature-settings:'calt'_0]",
        )}
      >
        <div className="flex items-start gap-3.25">
          {type === KimMessageType.AiReasoning && (
            <div className="mt-2.25 size-1.25 shrink-0 rounded-full bg-gray-300" />
          )}
          <div
            className={cn(
              'prose prose-sm prose-headings:my-3 prose-a:hover:text-blue-600 prose-a:duration-150 prose-a:text-blue-500 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-hr:my-6 prose-p:leading-normal prose-p:text-gray-900 prose-headings:font-semibold max-w-none',
              type === KimMessageType.AiReasoning && 'prose-p:text-gray-600 italic',
            )}
          >
            {rendered}
          </div>
        </div>

        {type === KimMessageType.Ai && isComplete && (
          <EndBlock
            copied={copied}
            copyToClipboard={copyToClipboard}
            factIds={factIds}
            referenceIndexMap={referenceIndexMap}
            onReferenceClick={onReferenceClick}
          />
        )}
      </div>
    </div>
  );
}
